"use client";

/**
 * Schedule Label Filter
 *
 * Shared chip filter for the schedule's labels, used by both the inline ScheduleRenderer and the
 * focused full-page calendar. Renders as a group of toggle buttons (`aria-pressed`) rather than an
 * ARIA radiogroup — a radiogroup would require arrow-key roving we don't implement, and toggle
 * buttons match the calendar's day-tab pattern. Each label chip is tinted in its own color (no
 * dot), so the filter doubles as the color key; the selected chip turns solid with luminance-picked
 * text. "All" is near-black when selected. Every chip is opaque, so it reads on dark sections too.
 */

import type React from "react";

import { getButtonTextColor } from "@/features/landing-pages/_logic/color-utils";
import { SCHEDULE_DEFAULT_LABEL_COLOR } from "../../../../_logic/constants";
import type { ScheduleLabelRow } from "../../../../_logic/types/db-types";
import { getScheduleLabelTint } from "../../../../_logic/utils/schedule-label-colors";

interface ScheduleLabelFilterProps {
  /** Labels to render as filter chips. Renders nothing when empty. */
  labels: ScheduleLabelRow[];
  /** Currently-selected label id, or `null` for the "All" chip. */
  activeLabelId: number | null;
  /** Called with the next selection (`null` = All). */
  onChange: (labelId: number | null) => void;
  /** Extra classes for the chip container (e.g. alignment overrides). */
  className?: string;
  /** Render smaller chips for tight chrome, such as the focused full-page schedule. */
  compact?: boolean;
  /** Keep labels on one horizontal scrolling row instead of wrapping. */
  scrollable?: boolean;
}

/**
 * Renders the label filter chips.
 *
 * @param props - See ScheduleLabelFilterProps.
 * @returns The chip group, or `null` when there are no labels.
 */
export function ScheduleLabelFilter({
  labels,
  activeLabelId,
  onChange,
  className = "",
  compact = false,
  scrollable = false,
}: ScheduleLabelFilterProps): React.ReactElement | null {
  if (labels.length === 0) return null;

  const chipClass = (isActive: boolean): string =>
    `shrink-0 touch-manipulation rounded-full font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-1 ${
      compact ? "px-2.5 py-1 text-xs" : "px-3.5 py-2 text-sm"
    } ${isActive ? "" : "hover:brightness-95"}`;
  const containerClass = scrollable
    ? `flex min-w-0 items-center gap-1.5 overflow-x-auto whitespace-nowrap scrollbar-none [&::-webkit-scrollbar]:hidden ${className}`
    : `flex flex-wrap items-center gap-1.5 ${className}`;

  return (
    <div
      className={containerClass}
      role="group"
      aria-label="Filter by label"
      style={scrollable ? { scrollbarWidth: "none" } : undefined}
    >
      <button
        type="button"
        aria-pressed={activeLabelId === null}
        onClick={() => onChange(null)}
        className={`${chipClass(activeLabelId === null)} ${
          activeLabelId === null ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-800"
        }`}
      >
        All
      </button>
      {labels.map((label) => {
        const color = label.color || SCHEDULE_DEFAULT_LABEL_COLOR;
        const isActive = activeLabelId === label.id;
        const tint = getScheduleLabelTint(color);
        return (
          <button
            type="button"
            key={label.id}
            aria-pressed={isActive}
            onClick={() => onChange(label.id)}
            className={chipClass(isActive)}
            style={
              isActive
                ? { backgroundColor: color, color: getButtonTextColor(color) }
                : { backgroundColor: tint.background, color: tint.text }
            }
          >
            {label.name}
          </button>
        );
      })}
    </div>
  );
}
